import { useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEqual } from 'lodash';
import {
    getUserAccessToClients,
    updateWiseClientIdHeader,
    updateWiseClientIdsHeader
} from '~/utils/app-utils';
import { setMainClient } from '~/reducers/mainClientSlice';
import { selectClientIds, selectClients } from '~/reducers/clientsSlice';
import { selectCurrentUser } from '~/reducers/currentUserSlice';
import { useSelectedClients } from '~/hooks/useSelectedClients';
import {
    selectSelectedClientIds,
    selectSelectedClients,
    setSelectedClients
} from '~/reducers/selectedClientsSlice';

const ClientProvider = () => {
    const dispatch = useDispatch();
    const { areAllMultiClientsSelected, userHasMultiAccess } =
        useSelectedClients();
    const clients = useSelector(selectClients);
    const clientIds = useSelector(selectClientIds);
    const selectedClients = useSelector(selectSelectedClients);
    const selectedClientIds = useSelector(selectSelectedClientIds);
    const currentUser = useSelector(selectCurrentUser);

    const userAccessToClients = useMemo(() => {
        const access = getUserAccessToClients();
        if (!access) {
            return;
        }
        return access.map((item: { client: string }) => item.client);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [clients, currentUser]);

    const mainClientInfo = useMemo(() => {
        let mainClientId;
        if (userAccessToClients) {
            const filteredAccess = userAccessToClients.filter((id: string) =>
                Boolean(selectedClients[id])
            );
            [mainClientId] = filteredAccess;
        } else {
            [mainClientId] = selectedClientIds;
        }
        return clients[mainClientId];
    }, [clients, selectedClients, selectedClientIds, userAccessToClients]);

    const selectFirstClient = useCallback(() => {
        const [firstClientId] = clientIds;
        if (firstClientId) {
            dispatch(setSelectedClients([firstClientId]));
        }
    }, [dispatch, clientIds]);

    const handleMultiClientAccess = useCallback(() => {
        if (userHasMultiAccess) {
            return;
        }
        selectFirstClient();
    }, [userHasMultiAccess, selectFirstClient]);

    useEffect(() => {
        if (!selectedClientIds.length || !clientIds.length) {
            return;
        }
        const validSelectedClientIds = selectedClientIds.filter((id) => {
            return clients[id];
        });
        if (!isEqual(validSelectedClientIds, selectedClientIds)) {
            dispatch(setSelectedClients(validSelectedClientIds));
        }
    }, [dispatch, clients, clientIds, selectedClientIds]);

    useEffect(() => {
        if (mainClientInfo) {
            const { id: mainClientId } = mainClientInfo;
            const clientIdRequestHeader = areAllMultiClientsSelected
                ? ''
                : mainClientId;
            updateWiseClientIdHeader(clientIdRequestHeader);
            updateWiseClientIdsHeader(selectedClientIds);
            dispatch(setMainClient(mainClientInfo));
        } else {
            handleMultiClientAccess();
        }
    }, [
        dispatch,
        selectedClients,
        clients,
        mainClientInfo,
        areAllMultiClientsSelected,
        selectedClientIds,
        handleMultiClientAccess
    ]);

    return null;
};

export { ClientProvider };
